import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { getFirebaseAnalytics } from '../config/firebase';

interface NormalizedRoute {
  /**
   * カスタムパラメータ `page_template` に送るルートテンプレート。
   * GA4 標準キー `page_path` とは衝突させない（`page_path` は GA4 が
   * `page_location` から自動派生するためカスタム計測では別名にする）。
   */
  pageTemplate: string;
  /** 動的セグメント値を併送するためのカスタムパラメータ */
  extraParams: Record<string, string>;
}

/**
 * 実 pathname をルートテンプレートと動的セグメント値に分解する。
 *
 * 動的ルートは `/fields/:fieldId` のみ。ルートが増えたらここに分岐を足す。
 * 生 path をそのまま送ると ID ごとに無限に分散してしまうため、
 * テンプレート化は必須。
 *
 * @param pathname - location.pathname
 * @returns 正規化結果
 */
const normalizePath = (pathname: string): NormalizedRoute => {
  const fieldDetailMatch = pathname.match(/^\/fields\/([^/]+)\/?$/);
  if (fieldDetailMatch) {
    return {
      pageTemplate: '/fields/:fieldId',
      extraParams: { field_id: fieldDetailMatch[1] },
    };
  }
  return { pageTemplate: pathname, extraParams: {} };
};

/**
 * SPA のページ遷移を Firebase Analytics に `page_view` として記録する hook。
 *
 * React Router の location 変化を購読し、テンプレート化したパスを
 * カスタムパラメータ `page_template` として、動的セグメント値を併送する。
 * Analytics が無効化されている環境（local 等）では何も送信しない。
 */
export const useAnalyticsPageView = (): void => {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const analytics = await getFirebaseAnalytics();
      if (!analytics || cancelled) return;
      const { pageTemplate, extraParams } = normalizePath(location.pathname);
      logEvent(analytics, 'page_view', {
        page_template: pageTemplate,
        page_location: window.location.href,
        page_title: document.title,
        ...extraParams,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);
};
